import { useState } from 'react';
import { InlineStack, Select } from '@customerio/pluma-components/react';
import { GrowingValueProvider } from './value-width';
import { fieldError, type BuilderError } from '@customerio/dryad';
import { isEmptyString } from '@/utils/string';
import { clearsValues, isTwoValueFilterOperator } from '../../attribute-filter-state';
import type { OperatorGroup } from '../../operator-groups';
import { timestampOperatorForVariant } from '../../operators';
import {
  isAbsoluteTimestampOperator,
  isTimestampInequalityOperator,
  normalizeTimestampOperatorForSelect,
} from '../../relative-timestamp';
import { TimestampVariantSelect } from './timestamp-editors';
import { ValueEditor } from './value-editor';
import { valueForOperatorChange } from './value-for-operator-change';
import { valueSourcePatchForOperator, valueSourcePlaceholder } from './value-source';
import { ValueSourceSelect } from './value-source-select';
import { CONDITION_CONTROL_GAP, type ConditionControlSize, type OperatorValuePatch, type ValueOption } from './types';

export type OperatorValueProps = {
  /** The attribute the condition names — the date tooltip reads it. */
  field: string;
  operator: string;
  value: string | null;
  value2: string | null;
  /**
   * The operator dropdown, derived by the row from the families its surface
   * declared, so the menu and the plugin's parse claim can't disagree.
   */
  operatorGroups: OperatorGroup[];
  valueOptions: ValueOption[];
  isLoadingValueOptions?: boolean;
  disabled?: boolean;
  /** The node's errors; the editor places them on the input each one names. */
  errors?: BuilderError[];
  /**
   * Shows the value's error whether or not the input was visited, for a host
   * that has asked to see every problem. Off by default.
   */
  showsAllErrors?: boolean;
  /** What the value field says while empty; "Value" by default. */
  valuePlaceholder?: string;
  /** What the value is compared against, on a row whose surface offers the choice. */
  valueSource?: string;
  /**
   * The value-source select's options. Absent means no select. Present, the
   * select shows while the operator can compare against another attribute.
   */
  valueSourceOptions?: ValueOption[];
  onChange: (patch: OperatorValuePatch) => void;
  /** The size of the operator select and the value editors, and the space between them; small by default. */
  size?: ConditionControlSize;
};

const NO_ERRORS: BuilderError[] = [];

/** The value inputs' errors, once the value has been visited or the host asks for every error. */
function shownValueErrors(errors: BuilderError[], isShown: boolean) {
  return {
    valueError: isShown ? fieldError(errors, 'value') : undefined,
    value2Error: isShown ? fieldError(errors, 'value2') : undefined,
  };
}

type OperatorChange = Required<Pick<OperatorValuePatch, 'operator' | 'value' | 'value2'>>;

/**
 * What picking an operator writes, or null when the pick changes nothing.
 *
 * The dropdown lists only the relative spelling of a paired timestamp
 * operator, so a pick keeps whichever variant the row is already on.
 */
function operatorChangePatch(
  nextOperator: string | null,
  current: { operator: string; value: string | null; value2: string | null },
): OperatorChange | null {
  if (nextOperator == null) {
    return null;
  }
  const { operator, value, value2 } = current;
  const variant = isAbsoluteTimestampOperator(operator) ? 'absolute' : 'relative';
  const transformed = timestampOperatorForVariant(nextOperator, variant);
  if (transformed === operator) {
    return null;
  }
  const keepsValue2 = !clearsValues(transformed, operator) && isTwoValueFilterOperator(transformed);
  return {
    operator: transformed,
    value: valueForOperatorChange(transformed, operator, value),
    value2: keepsValue2 ? value2 : null,
  };
}

/**
 * The operator select plus whichever value editor the chosen operator needs,
 * as siblings with no box of their own: they flow into the caller's row. The
 * value keeps its own width there.
 *
 * Which editors are reachable follows from the operator families a surface
 * declares — one offering only equality and existence never renders a date
 * picker, without having to say so here.
 */
export function OperatorValueControls({
  field,
  operator,
  value,
  value2,
  operatorGroups,
  valueOptions,
  isLoadingValueOptions,
  disabled,
  errors = NO_ERRORS,
  showsAllErrors = false,
  valuePlaceholder,
  valueSource,
  valueSourceOptions,
  onChange,
  size = 'sm',
}: OperatorValueProps) {
  // "Enter a value" renders only once the input has been visited — or from
  // the start when the row mounted holding a value, whose problems block the
  // commit either way.
  const [valueTouched, setValueTouched] = useState(() => !isEmptyString(value));

  // Always shown, untouched or not: an operator-tagged mismatch makes the
  // tree invalid — which stops the surface committing, so the row has to say
  // why rather than leave the editor looking like it simply stopped working.
  const operatorError = fieldError(errors, 'operator');
  const { valueError, value2Error } = shownValueErrors(errors, valueTouched || showsAllErrors);

  const handleOperatorChange = (nextOperator: string | null) => {
    const patch = operatorChangePatch(nextOperator, { operator, value, value2 });
    if (patch == null) {
      return;
    }
    // Switching operators must not inherit touched state; a carried non-blank
    // value keeps showing its problems while a cleared one starts clean.
    setValueTouched(!isEmptyString(patch.value));
    onChange({ ...patch, ...valueSourcePatchForOperator(patch.operator, valueSource, valueSourceOptions) });
  };

  const handleValueSourceChange = (patch: OperatorValuePatch) => {
    // A cleared value starts clean, like a value cleared by an operator change.
    setValueTouched((isTouched) => isTouched && patch.value === undefined);
    onChange(patch);
  };

  return (
    <>
      <Select
        data-testid="filter-operator-select"
        ariaLabel="Operator"
        size={size}
        options={operatorGroups}
        value={normalizeTimestampOperatorForSelect(operator)}
        error={operatorError}
        onChange={handleOperatorChange}
        isDisabled={disabled}
      />
      <ValueSourceSelect
        operator={operator}
        valueSource={valueSource}
        options={valueSourceOptions}
        disabled={disabled}
        onChange={handleValueSourceChange}
        size={size}
      />
      {isTimestampInequalityOperator(operator) ? (
        <TimestampVariantSelect disabled={disabled} operator={operator} value={value} onChange={onChange} size={size} />
      ) : null}
      <ValueEditor
        field={field}
        operator={operator}
        value={value}
        value2={value2}
        valueOptions={valueOptions}
        isLoadingValueOptions={isLoadingValueOptions}
        disabled={disabled}
        valueError={valueError}
        value2Error={value2Error}
        valuePlaceholder={valueSourcePlaceholder(valueSource) ?? valuePlaceholder}
        onChange={onChange}
        onValueBlur={() => setValueTouched(true)}
        size={size}
      />
    </>
  );
}

/**
 * The operator and value controls in a wrapping row of their own.
 *
 * A caller that already lays its controls out in a wrapping row renders
 * `OperatorValueControls` instead, so the operator can share a line with
 * whatever comes before it.
 */
export function OperatorValue(props: OperatorValueProps) {
  return (
    <InlineStack alignBlock="baseline" gap={CONDITION_CONTROL_GAP[props.size ?? 'sm']} wrap>
      {/* In a row of its own, the value fills what the row leaves. */}
      <GrowingValueProvider>
        <OperatorValueControls {...props} />
      </GrowingValueProvider>
    </InlineStack>
  );
}
